import React from 'react';
import Icon from '../../../components/AppIcon';

const TicketOverviewCard = ({ ticket, approval }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    return new Date(dateStr)?.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const d = new Date(deadline);
    const hoursLeft = (d - new Date()) / (1000 * 60 * 60);
    if (hoursLeft < 0) return { label: 'Expired', urgent: true };
    if (hoursLeft < 24) return { label: `${Math.round(hoursLeft)}h remaining`, urgent: true };
    return { label: d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), urgent: false };
  };

  const deadline = formatDeadline(approval?.deadline_at);
  const technicians = ticket?.ticket_technicians?.map(tt => tt?.technician?.name)?.filter(Boolean) || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Card Header */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="ClipboardList" size={16} color="var(--color-primary)" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wide">Service Ticket</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">{ticket?.ticket_number}</h2>
            <p className="text-sm text-gray-600 mt-0.5">{ticket?.service_type?.name || 'General Service'}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
              ticket?.priority?.name?.toLowerCase() === 'urgent' || ticket?.priority?.name?.toLowerCase() === 'high'
                ? 'bg-red-100 text-red-700'
                : ticket?.priority?.name?.toLowerCase() === 'medium' ?'bg-amber-100 text-amber-700' :'bg-gray-100 text-gray-600'
            }`}>
              <Icon name="Flag" size={11} />
              {ticket?.priority?.name || 'Normal'}
            </span>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Customer */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="User" size={15} color="#3b82f6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Customer</p>
              <p className="text-sm font-semibold text-gray-900">{ticket?.customer?.name || 'N/A'}</p>
              {ticket?.customer?.company && (
                <p className="text-xs text-gray-500">{ticket?.customer?.company}</p>
              )}
            </div>
          </div>

          {/* Scheduled Date */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="CalendarDays" size={15} color="#10b981" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Scheduled Date</p>
              <p className="text-sm font-semibold text-gray-900">{formatDate(ticket?.scheduled_date)}</p>
            </div>
          </div>

          {/* Technician */}
          {technicians?.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="HardHat" size={15} color="#8b5cf6" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Assigned Technician</p>
                <p className="text-sm font-semibold text-gray-900">{technicians?.join(', ')}</p>
              </div>
            </div>
          )}

          {/* Response Deadline */}
          {deadline && approval?.status === 'pending' && (
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${deadline?.urgent ? 'bg-red-50' : 'bg-orange-50'}`}>
                <Icon name="Clock" size={15} color={deadline?.urgent ? '#dc2626' : '#f97316'} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Response Deadline</p>
                <p className={`text-sm font-semibold ${deadline?.urgent ? 'text-red-600' : 'text-gray-900'}`}>{deadline?.label}</p>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        {ticket?.description && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-500 font-medium mb-2">Service Description</p>
            <p className="text-sm text-gray-700 leading-relaxed">{ticket?.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketOverviewCard;
